import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import Langs, { Localize } from '../../../app/lang/langs'
import { showErrorToast, showSuccessToast } from '../../../app/utils/helpers'
import { saveVLearnQuestion } from '../../../containers/vlearn/vlearnSlice'
import { vlearnType } from '../vlearnUi'

const MIN_OPTIONS = 2
const MAX_OPTIONS = 6

let optionKeySeed = 0
/** A render key that survives reordering and is never an option id — ids may be null. */
const nextKey = () => { optionKeySeed += 1; return `opt-${optionKeySeed}` }

const emptyOption = () => ({ key: nextKey(), id: null, text: '', isCorrect: false })

/**
 * V-Learning — writing one question.
 *
 * ── The validation here duplicates the server, and that is intentional ──────────────
 *
 * `VLearnQuizController.validateQuestionShape` is the guarantee: it refuses a question with
 * fewer than two options, a `true_false` that is not exactly two, no correct option, or
 * more than one correct option on a `single` or `true_false`. None of that is negotiable
 * server-side, because `gradeQuestion`'s exact-match rule makes a two-correct `single`
 * question literally unanswerable.
 *
 * What the client owes the author is not the guarantee — it is the sentence. A 422 saying
 * "A single question must have exactly 1 correct option" arrives after a round trip, with
 * no indication of which option to untick. Checking the same rules here turns that into an
 * inline error on the control that is wrong plus one panel naming every problem at once, so
 * the author fixes the question in a single pass instead of learning the rules one 422 at a
 * time. The duplication is the cost of a decent error message; it is not a substitute for
 * the server's check and must not drift from it.
 *
 * ── Why option ids are sent back ────────────────────────────────────────────────────
 *
 * `saveQuestion` UPDATEs an option that arrives with an id it recognises and INSERTs one
 * that does not. `vlearn_quiz_attempt_answer_selections` has a real foreign key to
 * `vlearn_quiz_options`, so an option that is re-created under a new id takes the learner's
 * recorded selection with it — and last month's attempt then reviews as though they
 * answered nothing. That is exactly V-Sécurité's defect 4, and it is avoided here only if
 * every existing option carries its `id` through the form and back out again. Dropping the
 * id would look harmless and would silently destroy assessment history.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.question the question being edited, or null to create
 * @param {number} props.idQuiz
 * @param {number} props.idCourse
 * @param {Function} props.onClose
 * @param {Function} props.onSaved
 */
export default function QuestionEditorDialog({ open, question, idQuiz, idCourse, onClose, onSaved }) {
    const dispatch = useDispatch()
    const lang = useSelector((state) => state.settings.lang)

    const theme = useTheme()
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

    const [text, setText] = useState('')
    const [type, setType] = useState('single')
    const [points, setPoints] = useState(1)
    const [explanation, setExplanation] = useState('')
    const [options, setOptions] = useState([emptyOption(), emptyOption()])
    const [touched, setTouched] = useState(false)
    const [saving, setSaving] = useState(false)

    // Re-seeded on every open so a cancelled edit never leaks into the next one.
    useEffect(() => {
        if (!open) return
        setTouched(false)
        setSaving(false)

        if (!question) {
            setText('')
            setType('single')
            setPoints(1)
            setExplanation('')
            setOptions([emptyOption(), emptyOption()])
            return
        }

        setText(question.question_text || '')
        setType(question.type || 'single')
        setPoints(Number(question.points) || 1)
        setExplanation(question.explanation || '')

        const existing = (question.options || [])
            // A retired option is not offered for editing: re-sending it would reactivate a
            // choice the author already withdrew, and the review screen keeps rendering it
            // from the attempt's own record either way.
            .filter((option) => option.active === undefined || !!Number(option.active))
            .map((option) => ({
                key: nextKey(),
                id: option.id,
                text: option.option_text || '',
                isCorrect: !!Number(option.is_correct)
            }))
        setOptions(existing.length >= MIN_OPTIONS ? existing : [emptyOption(), emptyOption()])
    }, [open, question])

    // ── Type transitions ────────────────────────────────────────────────────────────

    /**
     * Switching type rewrites the options so the form can never hold a shape the server
     * would refuse. Same behaviour as `QuestionForm.js`'s `handleTypeChange`, extended for
     * the type V-Sécurité does not have.
     */
    const handleTypeChange = useCallback((nextType) => {
        setType(nextType)

        if (nextType === 'true_false') {
            setOptions((previous) => {
                // Already a true/false pair — leave the author's own wording and their ids
                // alone rather than replacing two rows with two identical ones.
                if (previous.length === 2 && type === 'true_false') return previous
                return [
                    { key: nextKey(), id: null, text: Localize('VLEARN_QUESTION_TRUE', lang), isCorrect: true },
                    { key: nextKey(), id: null, text: Localize('VLEARN_QUESTION_FALSE', lang), isCorrect: false }
                ]
            })
            return
        }

        if (nextType === 'single') {
            // Only the first correct option survives: `single` accepts exactly one, and
            // silently keeping two would just move the 422 to the save button.
            setOptions((previous) => {
                let kept = false
                return previous.map((option) => {
                    if (option.isCorrect && !kept) {
                        kept = true
                        return option
                    }
                    return { ...option, isCorrect: false }
                })
            })
        }
    }, [lang, type])

    const toggleCorrect = useCallback((index) => {
        setOptions((previous) => previous.map((option, position) => {
            // One correct answer for single and true_false, free for multiple.
            if (type === 'single' || type === 'true_false') {
                return { ...option, isCorrect: position === index ? !option.isCorrect : false }
            }
            return position === index ? { ...option, isCorrect: !option.isCorrect } : option
        }))
    }, [type])

    const setOptionText = useCallback((index, value) => {
        setOptions((previous) => previous.map((option, position) =>
            position === index ? { ...option, text: value } : option))
    }, [])

    const addOption = useCallback(() => {
        setOptions((previous) => (previous.length >= MAX_OPTIONS ? previous : [...previous, emptyOption()]))
    }, [])

    const removeOption = useCallback((index) => {
        setOptions((previous) => (previous.length <= MIN_OPTIONS
            ? previous
            : previous.filter((option, position) => position !== index)))
    }, [])

    // ── Validation, mirroring validateQuestionShape ─────────────────────────────────

    const correctCount = options.filter((option) => option.isCorrect).length
    const isTrueFalse = type === 'true_false'
    const singleish = type === 'single' || isTrueFalse

    const errors = useMemo(() => {
        const found = []
        if (!text.trim()) found.push('VLEARN_QUESTION_ERR_TEXT')
        if (options.length < MIN_OPTIONS) found.push('VLEARN_QUESTION_ERR_TOO_FEW')
        if (isTrueFalse && options.length !== 2) found.push('VLEARN_QUESTION_ERR_TRUE_FALSE_TWO')
        if (options.some((option) => !option.text.trim())) found.push('VLEARN_QUESTION_ERR_EMPTY_OPTION')
        if (correctCount < 1) found.push('VLEARN_QUESTION_ERR_NO_CORRECT')
        if (singleish && correctCount > 1) found.push('VLEARN_QUESTION_ERR_ONE_CORRECT')
        return found
    }, [correctCount, isTrueFalse, options, singleish, text])

    const valid = errors.length === 0

    const handleSave = useCallback(async () => {
        setTouched(true)
        if (!valid || saving) return
        setSaving(true)

        const payload = {
            id_course: idCourse,
            id_quiz: idQuiz,
            question_text: text.trim(),
            type,
            points: Number(points) || 1,
            explanation: explanation.trim() ? explanation.trim() : null,
            options: options.map((option, index) => ({
                // The id is the whole point — see the docblock. `null` for a new option is
                // what tells the backend to INSERT rather than UPDATE.
                id: option.id || null,
                option_text: option.text.trim(),
                is_correct: option.isCorrect,
                order: index
            }))
        }
        if (question && question.id) payload.id_question = question.id

        const action = await dispatch(saveVLearnQuestion(payload))
        setSaving(false)

        const data = action && action.payload && action.payload.responseData
        if (!data || data.status !== 1) {
            const message = action && action.payload && action.payload.responseError
                && action.payload.responseError.data && action.payload.responseError.data.error
            showErrorToast(message || Localize('VLEARN_QUESTION_TOAST_SAVE_FAILED', lang))
            return
        }

        showSuccessToast(Localize('VLEARN_QUESTION_TOAST_SAVED', lang))
        if (onSaved) onSaved(data)
    }, [dispatch, explanation, idCourse, idQuiz, lang, onSaved, options, points, question, saving, text, type, valid])

    const showErrors = touched && !valid

    return (
        <Dialog
            open={!!open}
            onClose={(event, reason) => {
                // A form holding typed input must not be dismissed by a stray click on the
                // backdrop or a reflex Escape: that discarded everything, with no warning and
                // no undo. Closing is an explicit choice now — Cancel, or the save button.
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
                onClose()
            }}
            fullWidth
            maxWidth="sm"
            fullScreen={isSmall}
            slotProps={{ paper: { sx: { borderRadius: isSmall ? 0 : '18px' } } }}
        >
            <DialogTitle sx={{ ...vlearnType.sectionTitle, pb: 1 }}>
                <Langs str={question ? 'VLEARN_QUESTION_DIALOG_EDIT' : 'VLEARN_QUESTION_DIALOG_NEW'} />
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={3} sx={{ pt: 1 }}>
                    <TextField
                        label={Localize('VLEARN_QUESTION_FIELD_TEXT', lang)}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        error={touched && !text.trim()}
                        helperText={touched && !text.trim()
                            ? <Langs str="VLEARN_QUESTION_ERR_TEXT" /> : null}
                        fullWidth
                        required
                        multiline
                        minRows={2}
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                        <FormControl component="div" sx={{ flex: 1 }}>
                            <FormLabel component="div" id="vlearn-question-type-label" sx={labelSx}>
                                <Langs str="VLEARN_QUESTION_FIELD_TYPE" />
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby="vlearn-question-type-label"
                                value={type}
                                onChange={(event) => handleTypeChange(event.target.value)}
                            >
                                <FormControlLabel
                                    value="single"
                                    control={<Radio size="small" sx={radioSx} />}
                                    label={<Typography sx={{ fontSize: '0.88rem' }}><Langs str="VLEARN_QUESTION_TYPE_SINGLE" /></Typography>}
                                />
                                <FormControlLabel
                                    value="multiple"
                                    control={<Radio size="small" sx={radioSx} />}
                                    label={<Typography sx={{ fontSize: '0.88rem' }}><Langs str="VLEARN_QUESTION_TYPE_MULTIPLE" /></Typography>}
                                />
                                <FormControlLabel
                                    value="true_false"
                                    control={<Radio size="small" sx={radioSx} />}
                                    label={<Typography sx={{ fontSize: '0.88rem' }}><Langs str="VLEARN_QUESTION_TYPE_TRUE_FALSE" /></Typography>}
                                />
                            </RadioGroup>
                        </FormControl>

                        <TextField
                            label={Localize('VLEARN_QUESTION_FIELD_POINTS', lang)}
                            select
                            value={points}
                            onChange={(event) => setPoints(Number(event.target.value))}
                            helperText={<Langs str="VLEARN_QUESTION_FIELD_POINTS_HELP" />}
                            sx={{ minWidth: 160 }}
                        >
                            {[1, 2, 3, 4, 5, 6, 8, 10, 15, 20].map((value) => (
                                <MenuItem key={value} value={value}>{value}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    {/* ── Options ────────────────────────────────────────────────── */}
                    <FormControl component="div" sx={{ width: '100%' }}>
                        <FormLabel component="div" sx={labelSx}>
                            <Langs str="VLEARN_QUESTION_FIELD_OPTIONS" />
                        </FormLabel>
                        <FormHelperText sx={{ ...vlearnType.meta, ml: 0, mb: 1.5 }}>
                            <Langs str={singleish
                                ? 'VLEARN_QUESTION_OPTIONS_HELP_SINGLE'
                                : 'VLEARN_QUESTION_OPTIONS_HELP_MULTIPLE'} />
                        </FormHelperText>

                        <Stack spacing={1}>
                            {options.map((option, index) => (
                                <Stack key={option.key} direction="row" spacing={1} alignItems="flex-start">
                                    <Tooltip title={Localize('VLEARN_QUESTION_TOOLTIP_CORRECT', lang)}>
                                        {singleish ? (
                                            <Radio
                                                checked={option.isCorrect}
                                                onChange={() => toggleCorrect(index)}
                                                size="small"
                                                inputProps={{
                                                    'aria-label': Localize('VLEARN_QUESTION_ARIA_CORRECT', lang)
                                                        .replace('%n%', index + 1)
                                                }}
                                                sx={{ ...radioSx, mt: '6px' }}
                                            />
                                        ) : (
                                            <Checkbox
                                                checked={option.isCorrect}
                                                onChange={() => toggleCorrect(index)}
                                                size="small"
                                                inputProps={{
                                                    'aria-label': Localize('VLEARN_QUESTION_ARIA_CORRECT', lang)
                                                        .replace('%n%', index + 1)
                                                }}
                                                sx={{ ...radioSx, mt: '6px' }}
                                            />
                                        )}
                                    </Tooltip>

                                    <TextField
                                        value={option.text}
                                        onChange={(event) => setOptionText(index, event.target.value)}
                                        placeholder={Localize('VLEARN_QUESTION_OPTION_PLACEHOLDER', lang)
                                            .replace('%n%', index + 1)}
                                        error={touched && !option.text.trim()}
                                        size="small"
                                        fullWidth
                                        // `true_false` texts stay editable so a French quiz can
                                        // say "Vrai" and an English one "True" — the backend
                                        // only cares that there are two of them.
                                        inputProps={{ 'aria-label': Localize('VLEARN_QUESTION_ARIA_OPTION_TEXT', lang).replace('%n%', index + 1) }}
                                    />

                                    <Tooltip title={Localize('VLEARN_QUESTION_TOOLTIP_REMOVE_OPTION', lang)}>
                                        <IconButton
                                            size="small"
                                            onClick={() => removeOption(index)}
                                            disabled={options.length <= MIN_OPTIONS || isTrueFalse}
                                            aria-label={Localize('VLEARN_QUESTION_TOOLTIP_REMOVE_OPTION', lang)}
                                            sx={{
                                                mt: '4px', minWidth: 36, minHeight: 36, borderRadius: '9px',
                                                color: 'var(--red, rgb(251,105,98))',
                                                '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' },
                                                '&.Mui-disabled': { color: 'rgba(0,0,0,0.18)' }
                                            }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            ))}
                        </Stack>

                        {!isTrueFalse ? (
                            <Button
                                onClick={addOption}
                                disabled={options.length >= MAX_OPTIONS}
                                startIcon={<AddCircleOutlineIcon />}
                                sx={{ ...quietButtonSx, alignSelf: 'flex-start', mt: 1 }}
                            >
                                <Langs str="VLEARN_QUESTION_BTN_ADD_OPTION" />
                            </Button>
                        ) : null}

                        <FormHelperText sx={{ ...vlearnType.meta, ml: 0, mt: 1 }}>
                            <Langs str="VLEARN_QUESTION_OPTIONS_LIMIT" />
                        </FormHelperText>
                    </FormControl>

                    <TextField
                        label={Localize('VLEARN_QUESTION_FIELD_EXPLANATION', lang)}
                        value={explanation}
                        onChange={(event) => setExplanation(event.target.value)}
                        helperText={<Langs str="VLEARN_QUESTION_FIELD_EXPLANATION_HELP" />}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    {/* One panel listing everything that is wrong, so the author fixes the
                        question in one pass instead of discovering the rules one 422 at a
                        time. */}
                    {showErrors ? (
                        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                            <Stack spacing={0.25}>
                                {errors.map((key) => (
                                    <Typography key={key} sx={{ fontSize: '0.83rem' }}>
                                        <Langs str={key} />
                                    </Typography>
                                ))}
                            </Stack>
                        </Alert>
                    ) : null}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} sx={quietButtonSx}>
                    <Langs str="VLEARN_QUESTION_BTN_CANCEL" />
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    disabled={saving}
                    sx={primaryButtonSx}
                >
                    <Langs str={saving ? 'VLEARN_QUESTION_BTN_SAVING' : 'VLEARN_QUESTION_BTN_SAVE'} />
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const labelSx = {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-primary, #111318)',
    mb: 0.5,
    '&.Mui-focused': { color: 'var(--text-primary, #111318)' }
}

const radioSx = {
    color: 'rgba(0,0,0,0.26)',
    '&.Mui-checked': { color: 'var(--orange, rgb(255,144,21))' }
}

const primaryButtonSx = {
    borderRadius: '12px',
    px: 3,
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 650,
    fontSize: '0.9rem',
    backgroundColor: 'var(--orange, rgb(255,144,21))',
    color: '#fff',
    '&:hover': { backgroundColor: '#e07d0d' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' },
    '&.Mui-disabled': { backgroundColor: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.34)' }
}

const quietButtonSx = {
    borderRadius: '12px',
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.88rem',
    color: 'var(--text-secondary, #4b5263)',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}
